import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import { DateRenderer, DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isNotEmpty } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { Request } from '@/model/Request.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { web as webRoute } from '@/support/route';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface RequestsProps {
  requests?: Array<Request>;
  study: Study;
}

const Requests: React.FC<RequestsProps> = ({ requests, study }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hasWriteAccess = study && Auth.isWriteUserForStudy(study);
  const hasRequests = _isNotEmpty(requests);

  const columns: Array<TanStackColumnDef<Request>> = [
    {
      id: 'request_template.name',
      header: 'Name',
      accessorFn: (row: Request) => row.request_template?.name,
      size: 180,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Request } }) =>
        hasWriteAccess ? (
          <Link to={webRoute('studies.requests.edit', { id: study.id, requestId: original.id })} className="link blue">
            {original.request_template?.name}
          </Link>
        ) : (
          original.request_template?.name
        ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Request } }) =>
        original.status === 'in_progress' ? 'In Progress' : original.status === 'complete' ? 'Complete' : 'Pending',
    },
    {
      id: 'due_date',
      header: 'Due date',
      accessorKey: 'due_date',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => (
        <DateRenderer value={original.due_date} defaultResponse="-" />
      ),
    },
    {
      id: 'assigned.name',
      header: 'Requestee',
      accessorFn: (row: Request) => row.assigned_user?.name,
      enableSorting: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Request } }) => original.assigned_user?.name || 'Unassigned',
    },
    {
      id: 'user.name',
      header: 'Requestor',
      accessorFn: (row: Request) => row.user?.name,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => original.user?.name,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => <DateTimeRenderer value={original.created_at} />,
    },
  ];

  const openSelectRequestsModal = () => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'SELECT_STUDY_REQUESTS',
      props: {
        assignedRequests: requests,
        handleCallback: (requestId: ID) => {
          dispatch({ type: 'CLOSE_MODAL' });
          navigate(webRoute('studies.requests', { id: study.id, requestTemplateId: requestId }));
        },
      },
    });
  };

  return hasRequests ? (
    <div className="ui-card" data-test-component="Requests" data-test-element="container">
      <div className="flex justify-end pa3">
        <Button onClick={openSelectRequestsModal} disabled={!hasWriteAccess}>
          Add new
        </Button>
      </div>
      <div className="bt b--moon-gray">
        <Table data={requests} columns={columns} />
      </div>
    </div>
  ) : (
    <div data-test-component="Requests" data-test-element="no-data-container">
      <NoDataCard
        title="Your requests will appear here"
        text="Request templates can be added and configured to your requirements without affecting the original template."
        onLinkClick={openSelectRequestsModal}
        disabled={!hasWriteAccess}
        btnTxt="Add a request"
        openModal
      />
    </div>
  );
};

export default Requests;
